import "./AddOrEditReturnProduct.css";
import WestIcon from "@mui/icons-material/West";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { Checkbox } from "@mui/material";
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { BatchNumberObj, QuantityType } from "../utils/CreateClaimUtils";
import CustomAutocomplete from "../../../components/dms_common/CustomAutocomplete";
import CustomCsPcInput from "../../../components/dms_common/CustomCsPcInput";
import { ReactComponent as Warning } from "../../../assets/image/svg/warning.svg";
import { getStockData, getPtd, getClaims } from "../../../services/claimService";
import { getConfigData } from "../../../services/configurationService";
import { getDataFromLocalDb } from "../../../services/dbService";
import { useSelector, useDispatch } from "react-redux";
import {
  setBatchIdsExpanded,
  setCheckedBatches,
  setReturnProductObject,
  setReturnProductObjectInitial,
  setReturnProductObjectOriginal,
  setReturnProductsArray,
  setSelectedReturnProductArrayIdx,
  setEditedBatches
} from "../../../stateManagement/actions/claims";
import { openPopup } from "../../../stateManagement/actions/popupActions";
import { Loader } from "../../../components/loader/Loader";
import { logAnalyticsEvent, logLoadTrace } from "../../../../src/firebase";

const BatchListItem = ({
  batch,
  isSelected,
  isChanged,
  onCheckboxChange,
  isChecked,
  viewOnly,
  caseUnitLabel,
  otherUnitLabel,
  packUnitLabel,
}: {
  batch: any;
  isSelected: boolean;
  isChanged: boolean;
  onCheckboxChange: (checked: boolean) => void;
  isChecked: boolean;
  viewOnly: boolean;
  caseUnitLabel: string;
  otherUnitLabel: string;
  packUnitLabel: string;
}) => {
  const { editedBatches } = useSelector((state: any) => state.claims);
  const skuCode = useSelector((state: any) => state.claims.returnProductObject?.skuCode);
  const showCheckIcon = useMemo(() => {
    if (!skuCode || !batch?.id) return false;
    return editedBatches.some(
      (item: any) => item.skuCode === skuCode && item.batchIds?.includes(batch.id)
    );
  }, [editedBatches, skuCode, batch?.id]);

  return (
    <div className={`batchContainer ${isSelected && "batch-selected"}`}>
      <span title={batch.batchId === "Unbatched Qty"
        ? "Unbatched Qty"
        : `${batch.batchId}`}
        className={`batch-id-text ${isSelected && "batch-selected-text"}`}
      >
        {batch.batchId === "Unbatched Qty" ? (
          "Unbatched Qty"
        ) : (
          <>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
              {batch.batchId}
              {batch.stockType === "foc" && (
                <RenderSvg name="focIcon"/>
              )}
            </span>
          </>
        )}
      </span>
      {showCheckIcon && (
        <CheckCircleIcon
          style={{
            fontSize: "14px",
            verticalAlign: "middle",
            color: "#00C6B1",
          }}
        />
      )}
      <div className="batch-inputs-container">
        {caseUnitLabel && (
          <CustomCsPcInput
            value={batch.caseQty}
            onChange={() => null}
            label={caseUnitLabel}
            percentage={95}
            borderColor={isSelected ? "#00C6B1" : "#d9d9d9"}
            borderWidth="0.5px"
            readOnly={true}
          />
        )}
        {otherUnitLabel && (
          <CustomCsPcInput
            value={batch.otherQty}
            onChange={() => null}
            label={otherUnitLabel}
            percentage={95}
            borderColor={isSelected ? "#00C6B1" : "#d9d9d9"}
            borderWidth="0.5px"
            readOnly={true}
          />
        )}
        {packUnitLabel && (
          <CustomCsPcInput
            value={batch.pieceQty}
            onChange={() => null}
            label={packUnitLabel}
            percentage={95}
            borderColor={isSelected ? "#00C6B1" : "#d9d9d9"}
            borderWidth="0.5px"
            readOnly={true}
          />
        )}
      </div>

      <div className="batch-checkbox">
        <Checkbox
          sx={{
            color: "#DFE2E7",
            "&.Mui-checked": { color: "#00bfa5" },
            "& .MuiSvgIcon-root": { fontSize: "24px" },
          }}
          checked={isChecked}
          onChange={(e) => {
            e.stopPropagation();
            onCheckboxChange(e.target.checked);
          }}
          disabled={viewOnly}
        />
      </div>
    </div>
  );
};
interface BatchEditorProps {
  batch: BatchNumberObj;
  originalBatch: BatchNumberObj | undefined;
  onReset: (index: number) => void;
  onSave: (reasonDetails: any[]) => void;
  caseUnitLabel: string;
  otherUnitLabel: string;
  packUnitLabel: string;
}
const BatchEditor: React.FC<BatchEditorProps> = ({
  batch,
  originalBatch,
  onReset,
  onSave,
  caseUnitLabel,
  otherUnitLabel,
  packUnitLabel,
}) => {
  const { disableEditing } = useSelector((state: any) => state.claims);
  const [localBatch, setLocalBatch] = useState(batch);
  useEffect(() => {
    setLocalBatch(batch);
  }, [batch.id]);
  const validateInput = (value: string, key: QuantityType, index: number) => {
    if (value.startsWith("-")) return false;

    const parsedValue = value === "" ? 0 : parseInt(value, 10) || 0;

    const currentReason = localBatch.reasonDetails[index]?.reason;
    const originalReasonDetail = originalBatch?.reasonDetails?.find(
      (r) => r.reason === currentReason
    );

    if (originalReasonDetail && parsedValue > originalReasonDetail[key]) {
      openPopup(
        "Alert",
        `The ${key} quantity (${parsedValue}) cannot exceed the original ${key} quantity (${originalReasonDetail[key]}).`
      );
      return false;
    }

    return true;
  };
  const handleLocalChange = (index: number, key: QuantityType, value: string) => {
    if (!disableEditing && validateInput(value, key, index)) {
      const newReasons = [...localBatch.reasonDetails];
      newReasons[index] = {
        ...newReasons[index],
        [key]: value === "" ? 0 : parseInt(value, 10) || 0
      };
      setLocalBatch({
        ...localBatch,
        reasonDetails: newReasons
      });
    }
  };

  const handleSave = () => {
    onSave(localBatch.reasonDetails);
  };

  const handleLocalReset = (index: number) => {
    if (!originalBatch) return;

    const newReasons = [...localBatch.reasonDetails];
    newReasons[index] = { ...originalBatch.reasonDetails[index] };
    setLocalBatch({
      ...localBatch,
      reasonDetails: newReasons
    });
    onReset(index);
  };

  return (
    <div className="edit-right-purchase">
      <div className="edit-right-top">
        <div className="edit-right-top-header-purchase">
          <p className="batch-id-nos">{batch?.batchId}</p>
        </div>
        <div className="batch-ids-container">
          <div className="reasons-parent">
            <div className="reasons-container">
              {localBatch.reasonDetails.map((reason, index) => (
                <div className="label-input-container-edit" key={index}>
                  <p className="label-csps-edit" title={reason.reason}>{reason.reason}</p>
                  <div className="label-input-parent-edit">
                    <div className="cs-pc-container-edit">
                      {caseUnitLabel && (
                        <CustomCsPcInput
                          value={reason.caseQty}
                          onChange={(e) => handleLocalChange(index, "caseQty", e.target.value)}
                          label={caseUnitLabel}
                          percentage={100}
                          readOnly={disableEditing}
                        />
                      )}
                      {otherUnitLabel && (
                        <CustomCsPcInput
                          value={reason.otherQty}
                          onChange={(e) => handleLocalChange(index, "otherQty", e.target.value)}
                          label={otherUnitLabel}
                          percentage={100}
                          readOnly={disableEditing}
                        />
                      )}
                      {packUnitLabel && (
                        <CustomCsPcInput
                          value={reason.pieceQty}
                          onChange={(e) => handleLocalChange(index, "pieceQty", e.target.value)}
                          label={packUnitLabel}
                          percentage={100}
                          readOnly={disableEditing}
                        />
                      )}
                    </div>
                    {!disableEditing && (
                      <div style={{ marginTop: "6px", cursor: "pointer" }} onClick={() => handleLocalReset(index)}>
                        <RenderSvg name='undoIcon' />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="save-reasons-button">
              <button
                className="button-save"
                onClick={handleSave}
                style={{ backgroundColor: "#1B7EA0", borderColor: "#1B7EA0" }}
              >
                <span className="button-save-text">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddOrEditReturnProduct: React.FC = () => {
  const [expandedBatch, setExpandedBatches] = useState<any>("");
  const [batchIdMap, setBatchIdMap] = useState<any>({});
  const [banned, setBanned] = useState<any>([]);
  const [apiData, setApiData] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [skuCodeToSkuNameMap, setSkuCodeToSkuNameMap] = useState<{
    [key: string]: string | null;
  }>({});
  const [skuNameToSkuCodeMap, setSkuNameToSkuCodeMap] = useState<{
    [key: string]: string | null;
  }>({});

  const {
    returnProductsArray,
    returnProductObject,
    selectedReturnProductArrayIdx,
    batchIdsExpanded,
    editedBatches,
    checkedBatches,
    returnProductObjectInitial,
    returnProductObjectOriginal,
    viewOnly,
  } = useSelector((state: any) => state.claims);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, claimSubTypes, batchIdConfiguration, displayOtherUnitName, enableFocPurchaseReturnClaims} = ConfigDictionary;
  const fetchData = async () => {
    setLoading(true);
    try {
      const [stockData, claimsData] = await Promise.all([
        getStockData(),
        getClaims("", "", "PurchaseReturns")
      ]);
      const productData = await getDataFromLocalDb("Product");
      let products = {};
      if (displayOtherUnitName) {
        products = productData?.data?.[0]?.data;
      }
      const skuData = Object.values(productData?.data[0]?.data);
      const skuCodeToSkuNameMap: { [key: string]: string } = {};
      stockData.forEach((stock: any) => {
        skuCodeToSkuNameMap[stock.skuCode] = "";
      });

      skuData.forEach((product: any) => {
        if (skuCodeToSkuNameMap[product.batchCode] !== undefined) {
          skuCodeToSkuNameMap[product.batchCode] = product?.skuName || product?.skuDescription || "";
        }
      });
      const skuNameToSkuCodeMap: { [key: string]: string } = {};
      Object.entries(skuCodeToSkuNameMap).forEach(([skuCode, skuName]) => {
        if (skuName) {
          skuNameToSkuCodeMap[skuName] = skuCode;
        }
      });

      setApiData({ stockData, skuData, claimsData, products });
      setSkuCodeToSkuNameMap(skuCodeToSkuNameMap);
      setSkuNameToSkuCodeMap(skuNameToSkuCodeMap);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDataForReturnProduct = async () => {
      try {
        if (!apiData?.stockData) {
          logLoadTrace(fetchData, "claims_add_product_page_load_time");
        }
  
        let updatedBatchIdMap: any = {};
  
        if (apiData?.stockData) {
          let updatedReturnProduct;
  
          if (returnProductObject?.id && returnProductObject?.skuCode) {
            const originalDataEntry = returnProductObjectOriginal.find(
              (item: any) => item.skuCode === returnProductObject.skuCode
            );
  
            if (originalDataEntry) {
              let relevantBatches = originalDataEntry.originalData.batchIds;
              
              if (returnProductObject.stockType === "normal") {
                relevantBatches = originalDataEntry.originalData.batchIds.filter(
                  (b: any) => b.stockType !== "foc"
                );
              } else if (returnProductObject.stockType === "foc") {
                relevantBatches = originalDataEntry.originalData.batchIds.filter(
                  (b: any) => b.stockType === "foc"
                );
              }

              const mergedBatches = relevantBatches.map((originalBatch: any) => {
                const editedBatch = returnProductObject.batchIds?.find(
                  (b: any) => b.id === originalBatch.id
                );
                return {
                  ...originalBatch,
                  reasonDetails: editedBatch ? editedBatch.reasonDetails : originalBatch.reasonDetails || []
                };
              });
  
              updatedReturnProduct = {
                ...returnProductObject,
                batchIds: mergedBatches,
                stockType: returnProductObject.stockType
              };
            } else {
              updatedReturnProduct = updateReturnProductBatchIds(
                returnProductObject,
                apiData.stockData,
                apiData.claimsData,
                apiData.products
              );
            }
          } else {
            updatedReturnProduct = updateReturnProductBatchIds(
              returnProductObject,
              apiData.stockData,
              apiData.claimsData,
              apiData.products
            );
          }
  
          if (!updatedReturnProduct.skuCode) {
            updatedReturnProduct.skuCode = returnProductObject.skuCode;
          }
          if (!updatedReturnProduct.skuName) {
            updatedReturnProduct.skuName = returnProductObject.skuName || "";
          }
  
          const skuCode = updatedReturnProduct.skuCode;
          const existingEditedBatches = editedBatches.find(
            (item: any) => item.skuCode === skuCode
          );
          const previouslyEditedBatchIds = existingEditedBatches?.batchIds || [];
  
          previouslyEditedBatchIds.forEach((batchId: string) => {
            updatedBatchIdMap[batchId] = true;
          });
  
          if (!existingEditedBatches && !returnProductObject?.id) {
            dispatch(setReturnProductObjectOriginal([
              ...returnProductObjectOriginal,
              { skuCode, originalData: updatedReturnProduct },
            ]));
          }
  
          dispatch(setReturnProductObject(updatedReturnProduct));
          dispatch(setReturnProductObjectInitial(updatedReturnProduct));
  
          updatedReturnProduct.batchIds?.forEach((batch: any) => {
            updatedBatchIdMap[batch.id] = batchIdMap[batch.id] || false;
          });
        }
  
        setBatchIdMap(updatedBatchIdMap);
      } catch (error) {
        console.error("Error in fetchDataForReturnProduct:", error);
      }
    };
  
    fetchDataForReturnProduct();
  }, [returnProductObject?.skuCode, returnProductObject?.stockType]);

  useEffect(() => {
    if (expandedBatch && returnProductObject?.batchIds) {
      const updatedBatch = returnProductObject.batchIds.find(
        (b: any) => b.id === expandedBatch.id
      );
      if (updatedBatch) {
        setExpandedBatches(updatedBatch);
      }
    }
  }, [returnProductObject]);

const updateReturnProductBatchIds = (
  returnProductObject: any,
  stockData: any,
  claimsData: any,
  products: any
) => {
  const batchMap: Record<string, any> = {};

  if (stockData?.length) {
    const matchingStock = stockData.filter(
      (stock: any) => stock.skuCode === returnProductObject.skuCode
    );
    matchingStock.forEach((stock: any) => {
      const batchId = stock.batchId || "Unbatched Qty";
      const stockType = enableFocPurchaseReturnClaims ? stock.stockType || "normal" : "normal"; 
      const mapKey = `${batchId}_${stockType}`;

      if (!batchMap[mapKey]) {
        batchMap[mapKey] = {
          id: mapKey,
          caseQty: 0,
          otherQty: 0,
          pieceQty: 0,
          reasonDetails: [],
          stockType: stockType,
          batchId: batchId,
        };
      }

      if (stock.quantityType === "caseQty") {
        batchMap[mapKey].caseQty += stock.qty || 0;
      } else if (stock.quantityType === "otherQty") {
        batchMap[mapKey].otherQty += stock.qty || 0;
      } else if (stock.quantityType === "pieceQty") {
        batchMap[mapKey].pieceQty += stock.qty || 0;
      }

      const existingReason = batchMap[mapKey].reasonDetails.find(
        (r: any) => r.reason === stock.reason
      );

      if (existingReason) {
        existingReason.caseQty += stock.quantityType === "caseQty" ? stock.qty : 0;
        existingReason.pieceQty += stock.quantityType === "pieceQty" ? stock.qty : 0;
        existingReason.otherQty += stock.quantityType === "otherQty" ? stock.qty : 0;
      } else {
        batchMap[mapKey].reasonDetails.push({
          reason: stock.reason,
          caseQty: stock.quantityType === "caseQty" ? stock.qty : 0,
          pieceQty: stock.quantityType === "pieceQty" ? stock.qty : 0,
          otherQty: stock.quantityType === "otherQty" ? stock.qty : 0,
        });
      }
    });

    claimsData.forEach((claim: any) => {
      claim.claimDetails.forEach((detail: any) => {
        if (detail.skuCode !== returnProductObject.skuCode) return;
        const batchId = detail.batchCode;
        const reason = detail.remark;
        const stockType = enableFocPurchaseReturnClaims ? detail.extendedAttributes.stockType || "normal" : "normal";
        const mapKey = `${batchId}_${stockType}`;

        if (batchMap[mapKey]) {
          const claimedQty = detail.claimedQty.split(".").map(Number);

          if (claim.status === "REQUESTED") {
            const reasonDetail = batchMap[mapKey].reasonDetails.find(
              (r: any) => r.reason === reason
            );

            if (reasonDetail) {
              reasonDetail.caseQty = Math.max(0, reasonDetail.caseQty - claimedQty[0]);
              reasonDetail.pieceQty = Math.max(0, reasonDetail.pieceQty - claimedQty[1]);
              reasonDetail.otherQty = Math.max(0, reasonDetail.otherQty - claimedQty[2]);

              batchMap[mapKey].caseQty = Math.max(0, batchMap[mapKey].caseQty - claimedQty[0]);
              batchMap[mapKey].pieceQty = Math.max(0, batchMap[mapKey].pieceQty - claimedQty[1]);
              batchMap[mapKey].otherQty = Math.max(0, batchMap[mapKey].otherQty - claimedQty[2]);
            }
          }
        }
      });
    });
  }

  const filteredBatchMap = Object.fromEntries(
    Object.entries(batchMap).filter(
      ([_, totals]) =>
        totals.caseQty > 0 || totals.pieceQty > 0 || totals.otherQty > 0
    )
  );

  Object.values(filteredBatchMap).forEach((batch) => {
    batch.reasonDetails = batch.reasonDetails.filter(
      (reason:any) => reason.caseQty > 0 || reason.pieceQty > 0 || reason.otherQty > 0
    );
  });

  let customOtherUnitLabel = otherUnitLabel;
  if (displayOtherUnitName) {
    const product = products[returnProductObject.skuCode];
    if (product?.otherUnitName && product.otherUnitName.length > 0) {
      customOtherUnitLabel = product.otherUnitName;
    }
  }

  const updatedReturnProduct = {
    ...returnProductObject,
    otherUnitName: customOtherUnitLabel,
    batchIds: Object.values(filteredBatchMap).map((batch: any) => ({
      batchId: batch.batchId,
      id: batch.id,
      caseQty: batch.caseQty,
      otherQty: batch.otherQty,
      pieceQty: batch.pieceQty,
      reasonDetails: batch.reasonDetails,
      stockType: batch.stockType,
    })),
  };
  
  return updatedReturnProduct;
};

  const checkIfSkuExists = (skuCode: string) => {
    return returnProductsArray.some(
      (product: any) =>
        product.skuCode === skuCode &&
        product.id !== returnProductObject.id
    );
  };

  const handleChangeReturnProductObject = (key: string, value: any) => {
    let subObj = {};

    if (key === "skuCode") {
      subObj = {
        skuCode: value || "",
        skuName: value ? (skuCodeToSkuNameMap[value] || "N/A") : ""
      };

      if (value && checkIfSkuExists(value)) {
        openPopup(
          "Alert",
          `You have already added quantities for SKU Code ${value}. Kindly edit the existing quantities.`
        );
        return;
      }
    } else if (key === "skuName") {
      const skuCode = skuNameToSkuCodeMap[value] || "";
      subObj = {
        skuCode,
        skuName: value || ""
      };

      if (skuCode && checkIfSkuExists(skuCode)) {
        openPopup(
          "Alert",
          `You have already added quantities for SKU Code ${skuCode}. Kindly edit the existing quantities.`
        );
        return;
      }
    }

    const selectedReturnProduct = {
      ...returnProductObject,
      [key]: value || "",
      ...subObj,
    };

    if (key === "skuCode" || key === "skuName") {
      setExpandedBatches("");
      dispatch(setCheckedBatches([]));
      dispatch(setEditedBatches(editedBatches.filter((item: any) => item.skuCode !== returnProductObject.skuCode)));
      setBatchIdMap({});
    }
    dispatch(setReturnProductObject(selectedReturnProduct));
  };

  const handleCheckboxSelection = (batch: any, isChecked: boolean) => {
    const updatedReturnProductObject = JSON.parse(JSON.stringify(returnProductObject));
    let updatedCheckedBatches = [...checkedBatches];

    if (isChecked) {
      if (!updatedReturnProductObject.batchIds) {
        updatedReturnProductObject.batchIds = [];
      }

      const existingBatch = updatedReturnProductObject.batchIds.find(
        (b: any) => b.id === batch.id
      );

      if (!existingBatch) {
        const initialBatch = returnProductObjectInitial.batchIds?.find(
          (b: any) => b.id === batch.id
        );

        updatedReturnProductObject.batchIds.push({
          ...batch,
          reasonDetails: initialBatch?.reasonDetails || [],
        });
      }

      updatedCheckedBatches = [batch.id];
    } else {
      updatedCheckedBatches = updatedCheckedBatches.filter((id) => id !== batch.id);
    }
    dispatch(setReturnProductObject(updatedReturnProductObject));
    dispatch(setCheckedBatches(updatedCheckedBatches));
    setBanned([...banned, batch.id]);
  };

  const calculateTotalDamagedQuantities = (batches: any) => {
    let totalDamagedCs = 0;
    let totalDamagedPc = 0;
    let totalDamagedOther = 0;

    batches.forEach((batch: any) => {
      batch.reasonDetails.forEach((reason: any) => {
        const cs = parseInt(reason.caseQty, 10) || 0;
        const pc = parseInt(reason.pieceQty, 10) || 0;
        const other = parseInt(reason.otherQty, 10) || 0;

        totalDamagedCs += cs;
        totalDamagedPc += pc;
        totalDamagedOther += other;
      });
    });

    return { totalDamagedCs, totalDamagedPc, totalDamagedOther };
  };

  const getPtdValues = async (skuCode: string, pricingApiConfig: any): Promise<any> => {
    try {
      if (!batchIdConfiguration) {
        const localData = await getDataFromLocalDb("PrimaryCatalogue");
        if (localData?.data?.length > 0) {
          const product = localData.data.find((item: any) =>
            item.skuCode === skuCode
          );

          if (product) {
            return {
              ptdCs: product.casePtr || 0,
              ptdPc: product.packPtr || 0,
              ptdOther: product.otherUnitPtr || 0
            };
          }
        }
      }
      const ptdData = await getPtd(skuCode, pricingApiConfig);
      if (!ptdData || ptdData.length === 0) {
        throw new Error("Product details not found for the selected SKU code. Please update via MDM and try again.");
      }

      const result = processPriceData(ptdData, skuCode);
      return result;
    } catch (error) {
      console.error("Error getting PTD values:", error);
      openPopup("Alert", "Product details not found for the selected SKU code. Please update via MDM and try again.");
    }
  };

  const processPriceData = (features: any[], skuCode: string) => {
    if (batchIdConfiguration) {
      const batchPtdMap = features.reduce((map: any, feature: any) => {
        map[feature.batchId] = {
          ptdCs: feature.casePtr || 0,
          ptdPc: feature.packPtr || 0,
          ptdOther: feature.otherUnitPtr || 0
        };
        return map;
      }, {});

      return {
        ptdCs: 0,
        ptdPc: 0,
        ptdOther: 0,
        batchPtdMap
      };
    } else {
      const matchSkuCode = features.find((feature: any) =>
        feature.skuCode === skuCode
      );
      return {
        ptdCs: matchSkuCode.casePtr || 0,
        ptdPc: matchSkuCode.packPtr || 0,
        ptdOther: matchSkuCode.otherUnitPtr || 0
      };
    }
  };

  const validateUomPricing = (batches: any[], priceData: any) => {
    const errors: string[] = [];

    if (!batchIdConfiguration) {
      let uomsWithZero: string[] = [];

      const hasCaseQty = batches.some(batch =>
        batch.reasonDetails.some((reason: any) => (parseInt(reason.caseQty, 10) || 0) > 0)
      );
      const hasPieceQty = batches.some(batch =>
        batch.reasonDetails.some((reason: any) => (parseInt(reason.pieceQty, 10) || 0) > 0)
      );
      const hasOtherQty = batches.some(batch =>
        batch.reasonDetails.some((reason: any) => (parseInt(reason.otherQty, 10) || 0) > 0)
      );

      if (hasCaseQty && (priceData.ptdCs === 0 || priceData.ptdCs === undefined)) {
        uomsWithZero.push(caseUnitLabel);
      }
      if (hasOtherQty && (priceData.ptdOther === 0 || priceData.ptdOther === undefined)) {
        uomsWithZero.push(returnProductObjectInitial?.otherUnitName || otherUnitLabel);
      }
      if (hasPieceQty && (priceData.ptdPc === 0 || priceData.ptdPc === undefined)) {
        uomsWithZero.push(packUnitLabel);
      }

      if (uomsWithZero.length > 0) {
        errors.push(`${uomsWithZero.join(", ")} PTD is 0 for the selected SKU.`);
      }

      return errors;
    }

    batches.forEach((batch: any) => {
      const batchPtd = priceData.batchPtdMap?.[batch.batchId];
      if (!batchPtd) return;
      let uomsWithZero: string[] = [];
      batch.reasonDetails.forEach((reason: any) => {
        const caseQty = parseInt(reason.caseQty, 10) || 0;
        const pieceQty = parseInt(reason.pieceQty, 10) || 0;
        const otherQty = parseInt(reason.otherQty, 10) || 0;

        if (caseQty > 0 && (batchPtd.ptdCs === 0 || batchPtd.ptdCs === undefined)) { uomsWithZero.push(caseUnitLabel); }
        if (otherQty > 0 && (batchPtd.ptdOther === 0 || batchPtd.ptdOther === undefined)) { uomsWithZero.push(returnProductObjectInitial?.otherUnitName || otherUnitLabel); }
        if (pieceQty > 0 && (batchPtd.ptdPc === 0 || batchPtd.ptdPc === undefined)) { uomsWithZero.push(packUnitLabel); }
      });

      if (uomsWithZero.length > 0) {
        errors.push(`${uomsWithZero.join(", ")} PTD is 0 for Batch ID: ${batch.batchId}.`);
      }
    });
    return errors;
  };

  const handleClickSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!isFormValid()) {
        let errorMessage = "Please fill out all required fields before saving.";
        if (!skuCodeAndName()) {
          errorMessage = "Please select both SKU Code and SKU Name.";
        } else if (!returnProductObject?.batchIds?.length) {
          errorMessage = "Please select at least one Batch ID.";
        } else if (
          !returnProductObject.batchIds.every((batch: any) => {
            let totalCs = 0;
            let totalPc = 0;
            let totalOther = 0;
            batch.reasonDetails.forEach((reason: any) => {
              totalCs += parseInt(reason.caseQty, 10) || 0;
              totalPc += parseInt(reason.pieceQty, 10) || 0;
              totalOther += parseInt(reason.otherQty, 10) || 0;
            });
            return (
              totalCs <= batch.caseQty &&
              totalPc <= batch.pieceQty &&
              totalOther <= batch.otherQty
            );
          })
        ) {
          errorMessage = "Total damaged quantities cannot exceed claimable quantities.";
        }
        openPopup("Alert", errorMessage);
        return;
      }
  
      const newlyModifiedBatchIds = returnProductObject.batchIds
        .filter((batch: any) => batchIdMap[batch.id])
        .map((batch: any) => batch.id);
  
      const existingEntry = editedBatches.find(
        (item: any) => item.skuCode === returnProductObject.skuCode
      );
      const previouslyEditedBatchIds = existingEntry?.batchIds || [];
  
      const allEditedBatchIds = Array.from(new Set([
        ...previouslyEditedBatchIds,
        ...newlyModifiedBatchIds
      ]));
  
      const updatedEditedBatches = [
        ...editedBatches.filter((item: any) => item.skuCode !== returnProductObject.skuCode),
        {
          skuCode: returnProductObject.skuCode,
          batchIds: allEditedBatchIds
        }
      ];
  
      dispatch(setEditedBatches(updatedEditedBatches));
  
      let allRelevantBatches = returnProductObject.batchIds.filter((batch: any) =>
        allEditedBatchIds.includes(batch.id)
      );
  
      if (allRelevantBatches.length === 0) {
        openPopup("Alert", "No batches have been modified.");
        return;
      }
  
      const hasNormalBatches = allRelevantBatches.some((batch: any) => batch.stockType !== "foc");
      const hasFocBatches = allRelevantBatches.some((batch: any) => batch.stockType === "foc");
      const wasMixedProduct = returnProductObject.stockType === "mixed";
  
      let updatedReturnProductsArray = [...returnProductsArray];
      let newProducts = [];
  
      if (returnProductObject?.id) {
        updatedReturnProductsArray = updatedReturnProductsArray.filter(
          (product: any) => product.id !== returnProductObject.id
        );
      }
  
      if ((wasMixedProduct || (hasNormalBatches && hasFocBatches)) && enableFocPurchaseReturnClaims) {
        const normalBatches = allRelevantBatches.filter(
          (batch: any) => batch.stockType !== "foc"
        );
        const focBatches = allRelevantBatches.filter(
          (batch: any) => batch.stockType === "foc"
        );
  
        if (normalBatches.length > 0) {
          try {
            const normalProduct = await createNormalProduct(normalBatches, returnProductObject);
            newProducts.push(normalProduct);
          } catch (error: any) {
            console.error("Error creating normal product:", error);
            openPopup("Alert", error.message || "Error processing normal batches.");
            return;
          }
        }
  
        if (focBatches.length > 0 && enableFocPurchaseReturnClaims) {
          const focProduct = createFocProduct(focBatches, returnProductObject);
          newProducts.push(focProduct);
        }
      } 
      else {
        const productType = hasNormalBatches ? "normal" : "foc";
  
        if (productType === "normal") {
          try {
            const normalProduct = await createNormalProduct(allRelevantBatches, returnProductObject);
            newProducts.push(normalProduct);
          } catch (error: any) {
            console.error("Error creating normal product:", error);
            openPopup("Alert", error.message || "Error processing normal batches.");
            return;
          }
        } else if (enableFocPurchaseReturnClaims) {
          const focProduct = createFocProduct(allRelevantBatches, returnProductObject);
          newProducts.push(focProduct);
        }
      }
  
      if (newProducts.length === 0) {
        openPopup("Alert", "No valid products to save.");
        return;
      }
  
      updatedReturnProductsArray = [...updatedReturnProductsArray, ...newProducts];
  
      updatedReturnProductsArray = updatedReturnProductsArray.map((product: any, index: number) => ({
        ...product,
        id: index + 1
      }));
  
      dispatch(setReturnProductsArray(updatedReturnProductsArray));
  
      logAnalyticsEvent("claim_edit_saved", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        claim_type: "Damage Returns",
        claim_subtype: "Purchase Returns"
      });
  
      closeAndResetState();
    } catch (error) {
      console.error(error);
      openPopup("Alert", "Product details not found for the selected SKU code. Please update via MDM and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const createNormalProduct = async (batches: any[], productObject: any) => {
    try {
      const totals = calculateTotalDamagedQuantities(batches);
      const pricingApi = await getConfigData("dms_configuration", "pricingApiUrlPurchaseReturnsClaims");
      const pricingApiConfig = pricingApi?.[0]?.configValue ?? [];
      const priceData = await getPtdValues(productObject.skuCode, pricingApiConfig);
      if (!priceData) {
        throw new Error("Product details not found for the selected SKU code. Please update via MDM and try again.");
      }
      if (batchIdConfiguration && priceData?.batchPtdMap) {
        const missingBatchIds = batches
          .filter((batch: any) => {
            const hasQty = batch.reasonDetails.some((reason: any) =>
              (parseInt(reason.caseQty, 10) || 0) > 0 ||
              (parseInt(reason.pieceQty, 10) || 0) > 0 ||
              (parseInt(reason.otherQty, 10) || 0) > 0
            );
            return hasQty && !priceData.batchPtdMap[batch.batchId];
          })
          .map((batch: any) => batch.batchId);
                  
        if (missingBatchIds.length > 0) {
          const label = missingBatchIds.length > 1 ? "Batch IDs" : "Batch ID";
          throw new Error(`Product details not found for the ${label} ${missingBatchIds.join(', ')}. Please update via MDM and try again.`);
        }
      }
      
      const validationErrors = validateUomPricing(batches, priceData);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join("\n"));
      }
  
      let totalClaimAmount = 0;
      const processedBatches = batches.map((batch: any) => {
        const batchPtd = batchIdConfiguration && priceData.batchPtdMap?.[batch.batchId]
          ? priceData.batchPtdMap[batch.batchId]
          : {
            ptdCs: priceData.ptdCs || 0,
            ptdPc: priceData.ptdPc || 0,
            ptdOther: priceData.ptdOther || 0
          };
    
        const batchClaimAmount =
          (batchPtd.ptdCs || 0) * batch.reasonDetails.reduce((sum: number, r: any) => sum + (parseInt(r.caseQty, 10) || 0), 0) +
          (batchPtd.ptdPc || 0) * batch.reasonDetails.reduce((sum: number, r: any) => sum + (parseInt(r.pieceQty, 10) || 0), 0) +
          (batchPtd.ptdOther || 0) * batch.reasonDetails.reduce((sum: number, r: any) => sum + (parseInt(r.otherQty, 10) || 0), 0);
  
        totalClaimAmount += batchClaimAmount;
  
        return {
          ...batch,
          ...(batchIdConfiguration ? batchPtd : {}),
          batchClaimAmount
        };
      });
    
      if (totalClaimAmount === 0) {
        throw new Error("Total claim amount cannot be 0.");
      }
  
      return {
        id: 0,
        skuCode: productObject.skuCode,
        skuName: productObject.skuName,
        otherUnitName: productObject.otherUnitName,
        stockType: "normal",
        ...totals,
        ptdCs: priceData.ptdCs,
        ptdPc: priceData.ptdPc,
        ptdOther: priceData.ptdOther,
        batchIdsCount: batches.length,
        totalClaimAmount,
        batchIds: processedBatches,
      };
    } catch (error) {
      console.error('Error in createNormalProduct:', error);
      throw error;
    }
  };
  
  const createFocProduct = (batches: any[], productObject: any) => {
    const totals = calculateTotalDamagedQuantities(batches);
    
    const processedBatches = batches.map((batch: any) => ({
      ...batch,
      ptdCs: 0,
      ptdPc: 0,
      ptdOther: 0,
      batchClaimAmount: 0
    }));
  
    return {
      id: 0,
      skuCode: productObject.skuCode,
      skuName: productObject.skuName,
      otherUnitName: productObject.otherUnitName,
      stockType: "foc",
      ...totals,
      ptdCs: 0,
      ptdPc: 0,
      ptdOther: 0,
      batchIdsCount: batches.length,
      totalClaimAmount: 0,
      batchIds: processedBatches,
    };
  };

  const skuCodeAndName = () => {
    return (
      returnProductObject?.skuCode?.length &&
      returnProductObject?.skuName?.length
    );
  };

  const isFormValid = () => {
    if (!returnProductObject?.batchIds?.length || !skuCodeAndName()) {
      return false;
    }

    const hasNewEdits = returnProductObject.batchIds.some(
      (batch: any) => batchIdMap[batch.id] === true
    );

    const hasPreviousEdits = editedBatches.some(
      (item: any) => item.skuCode === returnProductObject.skuCode &&
        item.batchIds.length > 0
    );

    return hasNewEdits || hasPreviousEdits;
  };

  const handleSaveDamageQty = (batchId: number, reasonDetails: any[]) => {
    const batch = returnProductObject.batchIds.find((b: any) => b.id === batchId);
  
    if (batch && batch.stockType==="foc") {
      const allQuantitiesZero = reasonDetails.every((reason: any) => {
        const caseQty = parseInt(reason.caseQty, 10) || 0;
        const pieceQty = parseInt(reason.pieceQty, 10) || 0;
        const otherQty = parseInt(reason.otherQty, 10) || 0;
        return caseQty === 0 && pieceQty === 0 && otherQty === 0;
      });
  
      if (allQuantitiesZero) {
        openPopup(
          "Alert", 
          "Edited quantities cannot be 0 for foc batches. Please enter valid quantities."
        );
        return; 
      }
    }
    const batchIndex = returnProductObject.batchIds.findIndex(
      (b: any) => b.id === batchId
    );
  
    if (batchIndex === -1) return;
  
    const updatedReturnProductObject = {
      ...returnProductObject,
      batchIds: returnProductObject.batchIds.map((batch: any) => {
        if (batch.id === batchId) {
          return {
            ...batch,
            reasonDetails,
            caseQty: reasonDetails.reduce((sum, r) => sum + (parseInt(r.caseQty, 10) || 0), 0),
            pieceQty: reasonDetails.reduce((sum, r) => sum + (parseInt(r.pieceQty, 10) || 0), 0),
            otherQty: reasonDetails.reduce((sum, r) => sum + (parseInt(r.otherQty, 10) || 0), 0),
          };
        }
        return batch;
      }),
    };
  
    dispatch(setReturnProductObject(updatedReturnProductObject));
  
    const existingEntryIndex = editedBatches.findIndex(
      (item: any) => item.skuCode === returnProductObject.skuCode
    );
  
    const updatedEditedBatches = existingEntryIndex >= 0
      ? [...editedBatches]
      : [...editedBatches, { skuCode: returnProductObject.skuCode, batchIds: [] }];
  
    if (existingEntryIndex >= 0) {
      updatedEditedBatches[existingEntryIndex].batchIds = Array.from(
        new Set([...updatedEditedBatches[existingEntryIndex].batchIds, batchId])
      );
    } else {
      updatedEditedBatches[updatedEditedBatches.length - 1].batchIds = [batchId];
    }
  
    dispatch(setEditedBatches(updatedEditedBatches));
    setBatchIdMap((prev: any) => ({ ...prev, [batchId]: true }));

    setExpandedBatches("");
    dispatch(setCheckedBatches([]));
    dispatch(setBatchIdsExpanded(false));
    setBanned(banned.filter((ban: any) => ban !== batchId));
  };

  const handleResetReturnReasonObject = (batchId: string, reasonIndex: number) => {
    const updatedReturnProductObject = JSON.parse(
      JSON.stringify(returnProductObject)
    );
    const batchToUpdate = updatedReturnProductObject.batchIds.find(
      (batch: any) => batch.id === batchId
    );

    if (batchToUpdate) {
      const originalBatch = returnProductObjectInitial.batchIds.find(
        (batch: any) => batch.id === batchId
      );

      if (originalBatch) {
        batchToUpdate.reasonDetails[reasonIndex] = {
          ...originalBatch.reasonDetails[reasonIndex],
        };
      }
    }
    dispatch(setReturnProductObject(updatedReturnProductObject));
  };

  const closeAndResetState = (cancel = false) => {
    navigate("/dashboard/claims/create/purchaseReturns");
    dispatch(setSelectedReturnProductArrayIdx(null));
    dispatch(setReturnProductObject({}));
    dispatch(setBatchIdsExpanded(false));
    dispatch(setCheckedBatches([]));
    setExpandedBatches("");
    setBatchIdMap({});
    dispatch(setReturnProductObjectInitial({}));
    if (cancel) {
      const hasModifications = Object.keys(batchIdMap).some(key => batchIdMap[key] === true) ||
        editedBatches.some((item: any) => item.skuCode === returnProductObject.skuCode);

      if (hasModifications) {
        logAnalyticsEvent("claim_edit_cancelled", {
          distributor_id: JSON.parse(localStorage.authContext).user.loginId,
          claim_type: "Damage Returns",
          claim_subtype: "Purchase Returns"
        });
      }
    }
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('purchaseReturns')) {
        dispatch(setReturnProductsArray([]));
        dispatch(setEditedBatches([]));
      }
    };
  }, []);

  return (
    <div className="add-or-return-product-container">
      {loading ? (
        <Loader />
      ) : (<>
        <header>
          <div>
            <WestIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                closeAndResetState(true);
              }}
            />
            <p>{claimSubTypes[1]} - Add Products</p>
          </div>
          <CloseIcon
            style={{ cursor: "pointer" }}
            onClick={() => {
              closeAndResetState(true);
            }}
          />
        </header>

        <div className="arpm-formfields">
          <div className="arpm-formfields-row-1">
            <div className="arpm-labelInput-container">
              <p className="arpm-label">
                SKU Code<span>*</span>
              </p>
              <CustomAutocomplete
                options={Object.keys(skuCodeToSkuNameMap)}
                getOptionLabel={(option) => option}
                value={returnProductObject?.skuCode || ""}
                label="Select SKU Code"
                onChange={(e: any, value: string | null) => {
                  handleChangeReturnProductObject("skuCode", value);
                }}
                name="skuCode"
                height="40px"
                color="#464646"
                fontSize="14px"
                disabled={viewOnly}
              />
            </div>
            <div className="arpm-labelInput-container ">
              <p className="arpm-label">
                SKU Name<span>*</span>
              </p>
              <CustomAutocomplete
                options={Object.values(skuCodeToSkuNameMap).filter(Boolean)}
                getOptionLabel={(option) => option}
                value={returnProductObject?.skuName || ""}
                label="Select SKU Name"
                onChange={(e: any, value: string | null) => {
                  handleChangeReturnProductObject("skuName", value);
                }}
                name="skuName"
                height="40px"
                color="#464646"
                fontSize="14px"
                disabled={viewOnly}
              />
            </div>
          </div>
          {(
            <div className="arpm-formfields-row-2">
              {/* Batch ID Label */}
              <p className="arpm-label-batch" style={{ width: "100px" }}>
                Batch ID :
              </p>

              {/* Batch List Container */}
              <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
                <div className="purchase-edit-left">
                  {skuCodeAndName() ? (
                    <div className="arpm-batches-list-container" style={{ flexShrink: 0 }}>

                      {
                        returnProductObjectInitial?.batchIds?.length > 0 ? (<>
                          <div className="batch-list-heading">
                            <span>Select a batch ID to edit damage quantity</span>
                          </div>
                            <div className="batches-list">
                              {returnProductObjectInitial.batchIds.map((batch: any) => {
                                const updatedBatch = returnProductObject?.batchIds?.find(
                                  (b: any) => b.id === batch.id
                                );
                                const currentBatch = updatedBatch || batch;

                                return (
                                  <BatchListItem
                                    key={batch.id}
                                    batch={currentBatch}
                                    isSelected={expandedBatch === batch.id}
                                    isChanged={batchIdMap[batch.id] || false}
                                    isChecked={checkedBatches.includes(batch.id)}
                                    onCheckboxChange={(checked: any) => {
                                      handleCheckboxSelection(currentBatch, checked);
                                      if (checked) {
                                        setExpandedBatches(currentBatch);
                                        dispatch(setBatchIdsExpanded(true));
                                      } else if (expandedBatch?.id === batch.id) {
                                        setExpandedBatches(null);
                                        dispatch(setBatchIdsExpanded(false));
                                      }
                                    }}
                                    viewOnly={viewOnly}
                                    caseUnitLabel={caseUnitLabel}
                                    otherUnitLabel={returnProductObjectInitial?.otherUnitName || otherUnitLabel}
                                    packUnitLabel={packUnitLabel}
                                  />
                                );
                              })}
                            </div>
                        </>
                        ) : (
                          <div className="batch-list-heading">
                            <span>No data found.</span>
                          </div>
                        )
                      }
                    </div>
                  ) : (
                    <div className="no-sku-selected-message">
                      <Warning style={{ marginBottom: "3px" }} />
                      <span>No SKU code entered</span>
                    </div>
                  )}
                </div>

                {/* Batch Editor Container */}
                {
                  expandedBatch && returnProductObject?.batchIds?.length ? (
                    <div className="arpm-right">
                      <BatchEditor
                        batch={expandedBatch}
                        originalBatch={returnProductObjectOriginal.find(
                          (item: any) => item.skuCode === returnProductObject.skuCode
                        )?.originalData?.batchIds?.find(
                          (b: any) => b.id === expandedBatch.id
                        )}
                        onReset={(index) => {
                          handleResetReturnReasonObject(expandedBatch.id, index);
                        }}
                        onSave={(reasonDetails) => {
                          handleSaveDamageQty(expandedBatch.id, reasonDetails);
                        }}
                        caseUnitLabel={caseUnitLabel}
                        otherUnitLabel={returnProductObject?.otherUnitName || otherUnitLabel}
                        packUnitLabel={packUnitLabel}
                      />
                    </div>
                  ) : (
                    ""
                  )
                }
              </div>
            </div>)}
        </div>
        <footer>
          <span></span>
          <div className="arpmButtons">
            <button
              className="button-reset"
              style={{
                ...(viewOnly ? { display: "none" } : {}),
              }}
              onClick={() => {
                const isAnyBatchChecked = Object.values(batchIdMap).some(
                  (isChecked) => isChecked
                );

                if (isAnyBatchChecked) {
                  const updatedBatchIdMap = Object.keys(batchIdMap).reduce(
                    (acc: any, batchId: string) => {
                      acc[batchId] = false;
                      return acc;
                    },
                    {}
                  );

                  setBatchIdMap(updatedBatchIdMap);

                  const updatedReturnProductObject = {
                    ...returnProductObjectInitial,
                    batchIds: returnProductObject.batchIds
                      .filter((batch: any) => updatedBatchIdMap[batch.id])
                      .map((batch: any) => ({
                        ...batch,
                        isChecked: false,
                        reasonDetails: [],
                      })),
                  };
                  dispatch(setReturnProductObject(updatedReturnProductObject));
                  dispatch(setCheckedBatches([]));
                  dispatch(setEditedBatches(editedBatches.filter((item: any) => item.skuCode !== returnProductObject.skuCode)));
                  logAnalyticsEvent("claim_edit_cancelled", {
                    distributor_id: JSON.parse(localStorage.authContext).user.loginId,
                    claim_type: "Damage Returns",
                    claim_subtype: "Purchase Returns"
                  });
                }
              }}
            >
              <span className="button-reset-text">Reset</span>
            </button>
            <button
              className="button-save"
              style={{
                ...(viewOnly ? { display: "none" } : {}),
                backgroundColor: isFormValid() ? "#00bfa5" : "#d3d3d3",
                cursor: isFormValid() ? "pointer" : "not-allowed",
                border: isFormValid() ? "#00bfa5" : "#d3d3d3",
              }}
              onClick={handleClickSave}
              disabled={!isFormValid()}
            >
              <span className="button-save-text">Submit</span>
            </button>
          </div>
        </footer> </>)}
    </div>
  );
};

export default AddOrEditReturnProduct;